import type { RunListResponse, RunSummary, AgentRunListResponse, AgentRunRecord, RunStatusValue } from './types';

export function normalizeRunListResponse(payload: unknown): RunListResponse {
  const record = asRecord(payload);
  const data = asRecord(record?.data);
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(record?.runs)
      ? record.runs
      : Array.isArray(data?.runs)
        ? data.runs
        : [];
  return { runs: (source as unknown[]).map(normalizeRunSummary) };
}

export function normalizeAgentRunListResponse(payload: unknown): AgentRunListResponse {
  const record = asRecord(payload);
  const data = asRecord(record?.data);
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(record?.runs)
      ? record.runs
      : Array.isArray(data?.runs)
        ? data.runs
        : [];
  return { runs: (source as unknown[]).map(normalizeAgentRunRecord) };
}

function normalizeRunSummary(input: unknown): RunSummary {
  const raw = asRecord(input) ?? {};
  return {
    id: str(raw.id ?? raw.run_id, 'run'),
    skill_id: str(raw.skill_id ?? raw.template_slug ?? raw.template_id ?? raw.playbook_id, ''),
    surface_name: str(raw.surface_name ?? raw.template_name ?? raw.playbook_name, ''),
    surface_slug: str(raw.surface_slug ?? raw.template_slug ?? raw.slug, ''),
    surface_category: str(raw.surface_category ?? raw.category, ''),
    target_name: strOrNull(raw.target_name ?? raw.metadata_name),
    target_type: strOrNull(raw.target_type ?? raw.metadata_type),
    status: str(raw.status, 'done') as RunStatusValue,
    started_at: str(raw.started_at ?? raw.created_at, new Date().toISOString()),
    completed_at: strOrNull(raw.completed_at),
    total_ms: numOrNull(raw.total_ms),
    source: str(raw.source, 'manual'),
  };
}

function normalizeAgentRunRecord(input: unknown): AgentRunRecord {
  const raw = asRecord(input) ?? {};
  return {
    id: str(raw.id ?? raw.run_id, 'run'),
    agent_id: str(raw.agent_id ?? raw.agent_slug ?? raw.slug, ''),
    org_id: str(raw.org_id, ''),
    surface_name: str(raw.surface_name ?? raw.agent_name ?? raw.name, ''),
    surface_slug: str(raw.surface_slug ?? raw.agent_slug ?? raw.slug, ''),
    surface_type: str(raw.surface_type ?? raw.agent_type, ''),
    target_name: strOrNull(raw.target_name ?? raw.metadata_name),
    target_type: strOrNull(raw.target_type ?? raw.metadata_type),
    status: str(raw.status, 'done') as RunStatusValue,
    started_at: str(raw.started_at ?? raw.created_at, new Date().toISOString()),
    completed_at: strOrNull(raw.completed_at),
    total_ms: numOrNull(raw.total_ms),
    source: str(raw.source, 'manual'),
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function str(value: unknown, fallback: string): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

function strOrNull(value: unknown): string | null {
  const text = str(value, '');
  return text || null;
}

function numOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}
